import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faMusic,
  faPlus,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';

export default function AllTracksModal({ open, onClose, onPlay, onAdd }) {
  const [tracks, setTracks] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addingIds, setAddingIds] = useState(() => new Set());

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    const token = localStorage.getItem('walmates-token');
    setLoading(true);
    setError('');
    fetch('/api/music/all-tracks', {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load All Tracks');
      }
      return response.json();
    }).then((rows) => {
      setTracks(Array.isArray(rows) ? rows : []);
    }).catch((err) => {
      if (err.name !== 'AbortError') setError(err.message || 'Could not load All Tracks');
    }).finally(() => setLoading(false));
    return () => controller.abort();
  }, [open]);

  const visibleTracks = useMemo(() => {
    const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!words.length) return tracks;
    return tracks.filter((track) => {
      const haystack = `${track.title || track.name || ''} ${track.artist || ''}`.toLowerCase();
      return words.every((word) => haystack.includes(word));
    });
  }, [query, tracks]);

  if (!open) return null;

  const addTrack = async (track) => {
    const id = track.video_id || track.id;
    if (!id || addingIds.has(id)) return;
    setAddingIds((prev) => new Set(prev).add(id));
    try {
      await onAdd(track);
      setTracks((prev) => prev.filter((item) => (item.video_id || item.id) !== id));
    } catch (_) {
      // The parent reports the request failure; keep the track available to retry.
    } finally {
      setAddingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="all-tracks-modal" role="dialog" aria-modal="true" aria-label="All Tracks">
      <header className="all-tracks-header">
        <button type="button" className="all-tracks-back" onClick={onClose} aria-label="Back to Music">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Music</span>
        </button>
        <div>
          <h2>All Tracks</h2>
          <p>{tracks.length} available</p>
        </div>
        <span className="all-tracks-header-spacer" />
      </header>

      <label className="all-tracks-search">
        <FontAwesomeIcon icon={faSearch} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter title or artist…"
          aria-label="Filter All Tracks"
        />
      </label>

      <div className="all-tracks-list">
        {loading && <div className="all-tracks-empty">Loading tracks…</div>}
        {!loading && error && <div className="all-tracks-empty is-error">{error}</div>}
        {!loading && !error && visibleTracks.length === 0 && (
          <div className="all-tracks-empty">{query ? 'No matching tracks' : 'You already have every track'}</div>
        )}
        {!loading && !error && visibleTracks.map((track) => {
          const id = track.video_id || track.id;
          const art = track.thumbnail_medium || track.thumbnail_high || track.thumbnail_default;
          const adding = addingIds.has(id);
          return (
            <div className="all-tracks-row" key={id}>
              <button
                type="button"
                className="all-tracks-art"
                onClick={() => addTrack(track)}
                disabled={adding}
                aria-label={`Add ${track.title || track.name || 'track'} to your library`}
              >
                {art ? <img src={art} alt="" /> : <FontAwesomeIcon icon={faMusic} />}
                <span className="all-tracks-add" aria-hidden="true">
                  {adding ? '…' : <FontAwesomeIcon icon={faPlus} />}
                </span>
              </button>
              <button type="button" className="all-tracks-meta" onClick={() => onPlay(track)}>
                <span className="all-tracks-title">{track.title || track.name || 'Untitled'}</span>
                <span className="all-tracks-artist">{track.artist || 'Unknown artist'}</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
